import { forwardRef, useImperativeHandle, useRef } from "react";
import { useAppSelector } from "../../../../../config/hooks";

export interface PricingRef {
  getHtml: () => string;
  getFullHtml: () => string;
}

interface PricingSectionProps {
  index: number;
  companyLogo?: string;
}

const PricingSection = forwardRef<PricingRef, PricingSectionProps>(
  ({ index, companyLogo }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    interface PricingItem {
      name: string;
      total_price: number;
      services?: { name: string }[];
    }

    const pricingData =
      (useAppSelector(
        (state) => state?.potentialDeal?.proposalBuilderData?.data?.pricing
      ) as PricingItem[]) || [];

    useImperativeHandle(ref, () => ({
      getHtml: () => containerRef.current?.outerHTML ?? "",
      getFullHtml: () => containerRef.current?.outerHTML ?? "",
    }));

    const subtotal = pricingData?.reduce(
      (acc, item) => acc + item?.total_price,
      0
    );

    const SALES_TAX_PERCENTAGE = 8.5;
    const salesTaxTotalAmount = subtotal * (SALES_TAX_PERCENTAGE / 100);
    const totalAmountIncludingTax = subtotal + salesTaxTotalAmount;
    const monthly = totalAmountIncludingTax / 12;

    return (
      <div className="pricingEditor" ref={containerRef}>
        <div className="header">
          <div className="proposal_title">
            <div className="page_counter"> 0{index} </div>
            <h4 style={{ marginLeft: "24px" }}> Pricing </h4>
          </div>
          <div className="logo_container">
            {companyLogo ? (
              <img
                src={companyLogo}
                className="temp_logo"
                alt="template logo"
              />
            ) : (
              <img
                src="/images/company/no_image_grey.png"
                className="temp_logo"
                alt="logo"
              />
            )}
          </div>
        </div>

        <div className="content">
          <p>
            This proposal includes all the services listed below. The total cost
            will be split into 12 equal monthly payments over the one-year term.
          </p>
          <div className="invoiceTableContainer">
            <table
              className="invoiceTable"
              width="100%"
              cellPadding={0}
              cellSpacing={0}
            >
              <thead>
                <tr style={{ backgroundColor: "#004b64", color: "white" }}>
                  <th style={{ textAlign: "left", padding: 12 }}>
                    Item description
                  </th>
                  <th style={{ padding: 12, textAlign: "right" }}>Price, $</th>
                </tr>
              </thead>
              <tbody>
                {pricingData.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: 10 }}>
                      {item.name}
                      {item.services && (
                        <ul style={{ paddingLeft: 16, margin: 0 }}>
                          {item.services.map((sub, subIdx) => (
                            <li key={subIdx}>{sub.name}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td
                      style={{
                        padding: 10,
                        textAlign: "right",
                      }}
                    >
                      $
                      {item.total_price
                        ? Number(item.total_price.toFixed(2)).toLocaleString()
                        : "0.00"}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: 12, fontWeight: 500 }}>Subtotal:</td>
                  <td style={{ padding: 12, textAlign: "right" }}>
                    $
                    {subtotal
                      ? Number(subtotal.toFixed(2)).toLocaleString()
                      : "0.00"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 12, fontWeight: 500 }}>
                    Monthly price:
                  </td>
                  <td style={{ padding: 12, textAlign: "right" }}>
                    $
                    {monthly
                      ? Number(monthly.toFixed(2)).toLocaleString()
                      : "0.00"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    Total price:
                  </td>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    $
                    {subtotal
                      ? Number(subtotal.toFixed(2)).toLocaleString()
                      : "0.00"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    Sales Tax ({SALES_TAX_PERCENTAGE}%):
                  </td>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    $
                    {subtotal
                      ? Number(
                          (subtotal * (SALES_TAX_PERCENTAGE / 100)).toFixed(2)
                        ).toLocaleString()
                      : "0.00"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    Total amount:
                  </td>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      fontSize: "11px",
                      textAlign: "right",
                    }}
                  >
                    $
                    {totalAmountIncludingTax
                      ? Number(
                          totalAmountIncludingTax.toFixed(2)
                        ).toLocaleString()
                      : "0.00"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
);

export default PricingSection;
